/**
 * Stores (Storage & Database) API client — wraps the `/datasets/*` routes
 * served by the backend, base-path mapped onto api-${stage}.zibby.app.
 *
 *   list() → { ok, stores: [{ storeId, name, type, description, schema,
 *                              status, createdAt }], canWrite }
 *
 * The `/datasets` registry is called with the signed-in user's own session
 * token plus `?projectId=` — the server checks the user belongs to that
 * project. (It used to take the project's apiToken read off GET /projects;
 * reads no longer return that token.)
 *
 * Stores are CREATED conversationally (via the agent / Copilot) — no create
 * client here. The browse surface is read-only EXCEPT the two deliberate
 * mutations the store detail page owns: sqlite inline cell edit (sqlWrite,
 * a bound prepared statement the backend gates) and store deletion (remove,
 * which requires an explicit confirm and purges the data server-side).
 */

import api from './api';

const BASE = '/datasets';

// Every /datasets call names its project; the session token (api default)
// identifies the person.
const scoped = (path, projectId) =>
  `${path}${path.includes('?') ? '&' : '?'}projectId=${encodeURIComponent(projectId)}`;

// File/docs listing is PAGINATED server-side (`nextCursor`, max 1000 keys per
// call). Rendering one page as the whole store is a SILENT truncation, so
// listAllFiles follows the cursor to the end — and when a store is genuinely
// bigger than this ceiling it reports `truncated: true` so the UI can say the
// list is partial instead of quietly cutting it off.
const LIST_CAP = 5000;

export const Stores = {
  /**
   * List the project's stores (databases / data stores).
   * @param {string} projectId - the project whose stores to list.
   * @param {{ withSize?: boolean }} [opts] - withSize opts into the live
   *   per-store size probe (slow — never on a first-paint path).
   * @returns {Promise<{ ok: boolean, stores: Array }>}
   */
  list(projectId, opts) {
    const q = opts && opts.withSize ? '?withSize=1' : '';
    return api.get(scoped(`${BASE}/stores${q}`, projectId));
  },

  /**
   * ONE store by id — the read the detail page makes. `withSize` runs the live
   * size/docCount probe for THIS store only, so it's a separate, non-blocking
   * second call after the page has painted.
   * @returns {Promise<{ ok, store, canWrite?, noDataAccess? }>}
   */
  get(projectId, storeId, opts) {
    const q = opts && opts.withSize ? '?withSize=1' : '';
    return api.get(scoped(`${BASE}/stores/${encodeURIComponent(storeId)}${q}`, projectId));
  },

  /** Delete a store AND its data (server purges). Confirm is explicit. */
  remove(projectId, storeId) {
    return api.delete(scoped(`${BASE}/stores/${encodeURIComponent(storeId)}`, projectId), {
      body: { confirm: true },
    });
  },

  // ── Browse endpoints (the store data viewer) ────────────────────────────────
  // All bounded + tenant-forced server-side.
  /** SQLite: read-only SQL (SELECT/PRAGMA/EXPLAIN only, enforced server-side). */
  sql(projectId, storeId, sql) {
    return api.post(scoped(`${BASE}/stores/${encodeURIComponent(storeId)}/sql`, projectId), { sql, readOnly: true });
  },
  /**
   * SQLite WRITE — a bound prepared statement (params), NOT read-only, so the
   * backend gates it (self-host: the member's dataWrite permission). Used only
   * by the detail page's inline cell editing.
   */
  sqlWrite(projectId, storeId, sql, params) {
    return api.post(scoped(`${BASE}/stores/${encodeURIComponent(storeId)}/sql`, projectId), { sql, params });
  },
  /** Dataset: structured query DSL (omit select → all columns). */
  query(projectId, storeId, body) {
    return api.post(scoped(`${BASE}/stores/${encodeURIComponent(storeId)}/query`, projectId), body);
  },
  /** File store: list one page of paths (server caps at 1000/page). */
  listFiles(projectId, storeId, body) {
    const b = typeof body === 'number' ? { limit: body } : (body || { limit: 50 });
    return api.post(scoped(`${BASE}/stores/${encodeURIComponent(storeId)}/list`, projectId), b);
  },
  /**
   * File store: the COMPLETE path list — follows `nextCursor` to the end (cap
   * LIST_CAP), so callers never mistake page one for the whole store.
   * @returns {Promise<{ files: Array<{path,size}>, truncated: boolean }>}
   */
  async listAllFiles(projectId, storeId, cap = LIST_CAP) {
    let files = [];
    let cursor = '';
    for (;;) {
      const body = cursor ? { limit: 1000, cursor } : { limit: 1000 };
      const r = await Stores.listFiles(projectId, storeId, body);
      files = files.concat((r && r.files) || []);
      if (files.length >= cap) return { files: files.slice(0, cap), truncated: true };
      if (r && r.nextCursor) { cursor = r.nextCursor; continue; }
      return { files, truncated: false };
    }
  },
  /** File store: fetch one file's content. */
  getFile(projectId, storeId, path) {
    return api.post(scoped(`${BASE}/stores/${encodeURIComponent(storeId)}/get`, projectId), { path });
  },
};

export default Stores;
